import { frameId } from "../assets/manifest.ts";
import { EMPTY_TILE, unpackTile } from "../document/packed-tile.ts";
import type { CompiledChunk } from "./chunk.ts";
import type { CompiledMap, CompiledWorld } from "./compile.ts";
import type { ManifestObjectAppearance } from "./manifest.ts";
import { deserialiseChunk } from "./serialise-chunk.ts";

/**
 * The frames a compiled world can draw (ADR-057 amendment, P1.7-T2), which is what its atlas packs. Read
 * from the compiled output rather than the document, so terrain resolution and behaviour lowering have
 * already decided every frame: the client chunks' tile cells and props, every state of every object
 * appearance, since a thing changes state while a player watches it, and every frame of every transition
 * animation between those states (ADR-058), since the client plays them by id. Looping animations are not
 * followed here; the atlas build knows them and closes the set over the ones these frames start.
 */

/**
 * Adds the frames one client chunk draws: each non-empty cell by the chunk's tileset table and its layer's
 * variant, exactly as the renderer derives it, and the frame of each prop.
 *
 * @param chunk The client chunk.
 * @param into The set to add to.
 * @throws Error when a cell names a tileset index outside the chunk's table.
 */
function addChunkFrames(chunk: CompiledChunk, into: Set<string>): void {
  for (const layer of chunk.layers) {
    if (layer.kind === "tile") {
      const seen = new Set<number>();
      for (const packed of layer.tiles) {
        if (packed === EMPTY_TILE || seen.has(packed)) {
          continue;
        }
        seen.add(packed);
        const { tilesetIndex, tile } = unpackTile(packed);
        const tileset = chunk.tilesets[tilesetIndex];
        if (tileset === undefined) {
          throw new Error(`chunk ${chunk.cx},${chunk.cy} of ${chunk.mapId} layer ${layer.id} names tileset index ${tilesetIndex}, but the chunk lists ${chunk.tilesets.length} tilesets`);
        }
        into.add(frameId(tileset, layer.variant, tile));
      }
    } else if (layer.kind === "prop") {
      for (const prop of layer.props) {
        into.add(frameId(prop.frame.tileset, null, prop.frame.tile));
      }
    }
  }
}

/**
 * Adds every frame of one object appearance: each state's, and each transition's.
 *
 * @param appearance The appearance.
 * @param into The set to add to.
 */
function addAppearanceFrames(appearance: ManifestObjectAppearance, into: Set<string>): void {
  for (const state of appearance.states) {
    into.add(frameId(state.frame.tileset, null, state.frame.tile));
  }
  for (const transition of appearance.transitions ?? []) {
    for (const frame of transition.frames) {
      into.add(frameId(frame.tileset, null, frame.tile));
    }
  }
}

/**
 * Lists the frame ids one compiled map's chunks draw (ADR-109 decision 11): the client chunks' cells and props, and
 * every frame of each appearance a client prop or a server chunk's entity names, since the page draws a thing by its
 * appearance.
 *
 * @param map The compiled map; a blank one draws nothing.
 * @param appearances The world's appearance table.
 * @returns Every frame id once, in code-unit order.
 * @throws Error when a chunk text is malformed, a cell names a tileset index outside its chunk's table, or a thing names
 *   an appearance the table lacks.
 */
export function framesUsedByMap(map: CompiledMap, appearances: readonly ManifestObjectAppearance[]): string[] {
  const frames = new Set<string>();
  const things = new Set<number>();
  for (const text of map.chunks) {
    const client = deserialiseChunk(text.client);
    addChunkFrames(client, frames);
    for (const layer of client.layers) {
      if (layer.kind === "prop") {
        for (const prop of layer.props) {
          if (prop.appearance !== undefined) {
            things.add(prop.appearance);
          }
        }
      }
    }
    for (const layer of deserialiseChunk(text.server).layers) {
      if (layer.kind === "entity") {
        for (const entity of layer.entities) {
          if (entity.appearance !== undefined) {
            things.add(entity.appearance);
          }
        }
      }
    }
  }
  for (const index of things) {
    const appearance = appearances[index];
    if (appearance === undefined) {
      throw new Error(`map ${map.id} names appearance ${index}, which the table lacks`);
    }
    addAppearanceFrames(appearance, frames);
  }
  return [...frames].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/**
 * Lists the frame ids a compiled world draws.
 *
 * @param compiled The compiled world.
 * @returns Every frame id once, in code-unit order.
 * @throws Error when a client chunk text is malformed or a cell names a tileset index outside its chunk's
 *   table.
 */
export function framesUsedBy(compiled: CompiledWorld): string[] {
  const frames = new Set<string>();
  for (const map of compiled.maps) {
    for (const chunk of map.chunks) {
      addChunkFrames(deserialiseChunk(chunk.client), frames);
    }
  }
  for (const appearance of compiled.objectAppearances) {
    addAppearanceFrames(appearance, frames);
  }
  return [...frames].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}
